import { isDayjs, type Dayjs } from 'dayjs'
import { ApplicationError } from '../error/ApplicationError'
import type { Serializable } from '../models/Serializable'

export class Assert {
  static unreachable = (value: never): never => {
    throw new Error(`Unhandled variant: ${value}`)
  }

  static isSerializable(value: unknown): value is Serializable {
    return (
      typeof value === 'object' &&
      value !== null &&
      typeof (value as Serializable).serialize === 'function' &&
      typeof (value as Serializable).deserialize === 'function'
    )
  }

  static dayjs(value: unknown): asserts value is Dayjs {
    if (!isDayjs(value)) {
      throw new ApplicationError('Value is not an instance of Dayjs')
    }
  }

  static instanceOf<T>(
    value: unknown,
    ctor: (abstract new (...args: never[]) => T) & { readonly name: string },
  ): asserts value is T {
    if (!(value instanceof ctor)) {
      throw new Error(`Expected value to be instance of ${ctor.name}`)
    }
  }

  static notNull<T>(value: T | null | undefined): asserts value is T {
    if (value === null || value === undefined) {
      throw new Error('Value is null or undefined')
    }
  }

  static number(value: unknown): asserts value is number {
    if (typeof value !== 'number') {
      throw new Error('Value is not a number')
    }
  }

  static string(value: unknown): asserts value is string {
    if (typeof value !== 'string') {
      throw new Error('Value is not a string')
    }
  }

  static isObject<T extends object = Record<string, unknown>>(value: unknown): asserts value is T {
    if (typeof value !== 'object' || value === null) {
      throw new Error('Value is not an object')
    }
  }
}
